import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Ban, KeyRound, Loader2, PlayCircle, ShieldPlus } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

import { useQueryClient } from '@tanstack/react-query';

import {
  useRoleTypesQuery,
  useAssignUserRoleMutation,
  useRevokeUserRoleMutation,
} from '@/client/api/admin/roles';
import {
  usePermissionRegistryQuery,
  useUserPermissionsQuery,
  useAssignUserPermissionMutation,
  useToggleUserPermissionMutation,
} from '@/client/api/admin/permissions';
import { useAdminUserHoldMutation } from '@/client/api/admin/users.mutation';
import { adminUserKeys } from '@/client/api/admin/users.query';
import type { AdminUserListRow } from '@/client/types/admin/users-orgs';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';

type ModalKind = 'role' | 'permission' | 'suspend' | null;

/** Stop the row's onClick (which opens the details drawer) from firing when a
 *  user interacts with the action controls or the modals rendered inside them. */
const swallow = (e: React.MouseEvent) => e.stopPropagation();

/** An icon button that disables itself (with an explanatory tooltip) when the
 *  user lacks `code`. The span wrapper keeps the tooltip hoverable while the
 *  button is disabled. */
const GatedIconButton = ({
  code,
  label,
  onClick,
  className,
  children,
}: {
  code: string;
  label: string;
  onClick: () => void;
  className: string;
  children: React.ReactNode;
}) => {
  const { allowed, isLoading, reason } = useGate(code);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={allowed ? 'inline-flex' : 'inline-flex cursor-not-allowed'}>
          <button
            type="button"
            className={`${className} disabled:opacity-40`}
            disabled={!allowed || isLoading}
            aria-disabled={!allowed || undefined}
            onClick={() => allowed && onClick()}
          >
            {children}
          </button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{allowed ? label : reason}</TooltipContent>
    </Tooltip>
  );
};

/* ─────── Reusable controlled dialogs (shared by the list icons and the
   details-page Access buttons). Bodies mount only while open so their queries
   don't fire until needed. ─────── */

export const ManageRolesDialog = ({
  row,
  open,
  onOpenChange,
}: {
  row: AdminUserListRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg" onClick={swallow}>
      {open && <AssignRoleBody row={row} onDone={() => onOpenChange(false)} />}
    </DialogContent>
  </Dialog>
);

export const ManagePermissionsDialog = ({
  row,
  open,
  onOpenChange,
}: {
  row: AdminUserListRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg" onClick={swallow}>
      {open && <AssignPermissionBody row={row} onDone={() => onOpenChange(false)} />}
    </DialogContent>
  </Dialog>
);

export const SuspendDialog = ({
  row,
  open,
  onOpenChange,
}: {
  row: AdminUserListRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent onClick={swallow}>
      {open && <SuspendBody row={row} onDone={() => onOpenChange(false)} />}
    </AlertDialogContent>
  </AlertDialog>
);

const UserRowActions = ({ row }: { row: AdminUserListRow }) => {
  const [modal, setModal] = useState<ModalKind>(null);
  const close = () => setModal(null);

  const iconBtn =
    'inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700';
  const isSuspended = row.user.admin_hold === 1;

  return (
    <div className="flex items-center justify-end gap-1" onClick={swallow}>
      <TooltipProvider delayDuration={200}>
        <GatedIconButton
          code={PERMISSIONS.role.create}
          label="Assign role"
          className={iconBtn}
          onClick={() => setModal('role')}
        >
          <ShieldPlus className="h-4 w-4" />
        </GatedIconButton>

        <GatedIconButton
          code={PERMISSIONS.systemPermissions}
          label="Assign permissions"
          className={iconBtn}
          onClick={() => setModal('permission')}
        >
          <KeyRound className="h-4 w-4" />
        </GatedIconButton>

        <GatedIconButton
          code={PERMISSIONS.user.edit}
          label={isSuspended ? 'Reactivate account' : 'Suspend account'}
          className={`${iconBtn} hover:bg-amber-50 hover:text-amber-700`}
          onClick={() => setModal('suspend')}
        >
          {isSuspended ? <PlayCircle className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
        </GatedIconButton>
      </TooltipProvider>

      <ManageRolesDialog row={row} open={modal === 'role'} onOpenChange={o => !o && close()} />
      <ManagePermissionsDialog
        row={row}
        open={modal === 'permission'}
        onOpenChange={o => !o && close()}
      />
      <SuspendDialog row={row} open={modal === 'suspend'} onOpenChange={o => !o && close()} />
    </div>
  );
};

/* ───────────────────────────── Assign role ───────────────────────────── */

const AssignRoleBody = ({ row, onDone }: { row: AdminUserListRow; onDone: () => void }) => {
  const { user, roles } = row;
  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || `user #${user.uid}`;
  const queryClient = useQueryClient();
  const { data: roleTypes = [], isLoading } = useRoleTypesQuery();
  const assign = useAssignUserRoleMutation();
  const revoke = useRevokeUserRoleMutation();

  // Currently-held roles: uartid -> uarid (the assignment id we revoke by).
  const uaridByUartid = useMemo(() => {
    const m = new Map<number, number>();
    for (const r of roles) if (r.active === 1 && !m.has(r.uartid)) m.set(r.uartid, r.uarid);
    return m;
  }, [roles]);
  const assigned = useMemo(() => new Set(uaridByUartid.keys()), [uaridByUartid]);

  // Start from what the user already has; the admin ticks/unticks freely.
  const [selected, setSelected] = useState<Set<number>>(() => new Set(assigned));
  const toggle = (uartid: number) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(uartid) ? next.delete(uartid) : next.add(uartid);
      return next;
    });

  const toAdd = [...selected].filter(id => !assigned.has(id));
  const toRemove = [...assigned].filter(id => !selected.has(id));
  const removeUarids = toRemove.map(id => uaridByUartid.get(id)!).filter(Boolean);
  const dirty = toAdd.length + toRemove.length > 0;
  const pending = assign.isPending || revoke.isPending;

  const handleSave = () => {
    if (!dirty) return;
    const run = async () => {
      const ops: Promise<unknown>[] = [];
      if (toAdd.length) ops.push(assign.mutateAsync({ uid: user.uid, uartids: toAdd }));
      for (const uarid of removeUarids) ops.push(revoke.mutateAsync({ uarid }));
      await Promise.all(ops);
      // The list endpoint carries each row's roles, so refresh it too.
      await queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    };
    toast.promise(run(), {
      loading: 'Updating roles…',
      success: () => {
        onDone();
        const parts = [
          toAdd.length ? `+${toAdd.length}` : null,
          toRemove.length ? `−${toRemove.length}` : null,
        ].filter(Boolean);
        return `Roles updated for ${fullName} (${parts.join(', ')})`;
      },
      error: err => (err as Error).message || 'Failed to update roles',
    });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Manage roles</DialogTitle>
        <DialogDescription>
          Tick to grant, untick to revoke for <span className="font-medium">{fullName}</span>.
        </DialogDescription>
      </DialogHeader>

      <div className="max-h-72 space-y-1 overflow-y-auto py-2">
        {isLoading &&
          Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full rounded-md" />
          ))}
        {!isLoading &&
          roleTypes.map(rt => {
            const isAssigned = assigned.has(rt.uartid);
            const isChecked = selected.has(rt.uartid);
            return (
              <label
                key={rt.uartid}
                className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={isChecked}
                  onChange={() => toggle(rt.uartid)}
                />
                <span className="flex-1">
                  <span className="font-medium text-gray-900">
                    {rt.description ?? rt.name}
                  </span>
                  {rt.description && (
                    <span className="ml-1 text-xs text-gray-400">({rt.name})</span>
                  )}
                </span>
                {isAssigned && isChecked && (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                    Assigned
                  </span>
                )}
                {isAssigned && !isChecked && (
                  <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600">
                    Will revoke
                  </span>
                )}
                {!isAssigned && isChecked && (
                  <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-600">
                    Will add
                  </span>
                )}
              </label>
            );
          })}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={pending || !dirty}>
          {pending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          Save changes
        </Button>
      </DialogFooter>
    </>
  );
};

/* ─────────────────────────── Assign permission ─────────────────────────── */

const AssignPermissionBody = ({ row, onDone }: { row: AdminUserListRow; onDone: () => void }) => {
  const { user } = row;
  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || `user #${user.uid}`;
  const queryClient = useQueryClient();
  const { data: permissions = [], isLoading } = usePermissionRegistryQuery({ page: 1, per_page: 1000 });
  const { data: userPerms = [] } = useUserPermissionsQuery(user.uid);
  const assign = useAssignUserPermissionMutation();
  const toggleAssignment = useToggleUserPermissionMutation();

  // Currently-held permissions: permid -> upermid (deactivated to revoke).
  const upermidByPermid = useMemo(() => {
    const m = new Map<number, number>();
    for (const p of userPerms) if (!m.has(p.permid)) m.set(p.permid, p.upermid);
    return m;
  }, [userPerms]);
  const assigned = useMemo(() => new Set(upermidByPermid.keys()), [upermidByPermid]);

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [seeded, setSeeded] = useState(false);
  useEffect(() => {
    if (!seeded && userPerms.length > 0) {
      setSelected(new Set(assigned));
      setSeeded(true);
    }
  }, [seeded, userPerms.length, assigned]);

  const toggle = (permid: number) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(permid) ? next.delete(permid) : next.add(permid);
      return next;
    });

  // Group the registry by category so a long list stays scannable.
  const grouped = useMemo(() => {
    const map = new Map<string, typeof permissions>();
    for (const p of permissions) {
      const key = p.category || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    return [...map.entries()];
  }, [permissions]);

  const toAdd = [...selected].filter(id => !assigned.has(id));
  const toRemove = [...assigned].filter(id => !selected.has(id));
  const removeUpermids = toRemove.map(id => upermidByPermid.get(id)!).filter(Boolean);
  const dirty = toAdd.length + toRemove.length > 0;
  const pending = assign.isPending || toggleAssignment.isPending;

  const handleSave = () => {
    if (!dirty) return;
    const run = async () => {
      const ops: Promise<unknown>[] = [];
      if (toAdd.length) ops.push(assign.mutateAsync({ uid: user.uid, permids: toAdd }));
      for (const upermid of removeUpermids)
        ops.push(toggleAssignment.mutateAsync({ upermid, active: false }));
      await Promise.all(ops);
      await queryClient.invalidateQueries({ queryKey: adminUserKeys.all });
    };
    toast.promise(run(), {
      loading: 'Updating permissions…',
      success: () => {
        onDone();
        const parts = [
          toAdd.length ? `+${toAdd.length}` : null,
          toRemove.length ? `−${toRemove.length}` : null,
        ].filter(Boolean);
        return `Permissions updated for ${fullName} (${parts.join(', ')})`;
      },
      error: err => (err as Error).message || 'Failed to update permissions',
    });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Manage permissions</DialogTitle>
        <DialogDescription>
          Tick to grant, untick to revoke for <span className="font-medium">{fullName}</span>.
        </DialogDescription>
      </DialogHeader>

      <div className="max-h-80 space-y-4 overflow-y-auto py-2">
        {isLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-full rounded-md" />
          ))}
        {!isLoading &&
          grouped.map(([category, perms]) => (
            <div key={category}>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                {category}
              </p>
              <div className="space-y-1">
                {perms.map(p => {
                  const isAssigned = assigned.has(p.permid);
                  const isChecked = selected.has(p.permid);
                  return (
                    <label
                      key={p.permid}
                      className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300"
                        checked={isChecked}
                        onChange={() => toggle(p.permid)}
                      />
                      <span className="flex-1">
                        <span className="font-medium text-gray-900">{p.name}</span>
                        <span className="ml-1 font-mono text-xs text-gray-400">{p.code}</span>
                      </span>
                      {isAssigned && isChecked && (
                        <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                          Assigned
                        </span>
                      )}
                      {isAssigned && !isChecked && (
                        <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600">
                          Will revoke
                        </span>
                      )}
                      {!isAssigned && isChecked && (
                        <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-600">
                          Will add
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={pending || !dirty}>
          {pending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          Save changes
        </Button>
      </DialogFooter>
    </>
  );
};

/* ───────────────────────────── Suspend / lift ───────────────────────────── */

const SuspendBody = ({ row, onDone }: { row: AdminUserListRow; onDone: () => void }) => {
  const { user } = row;
  const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() || `user #${user.uid}`;
  const isSuspended = user.admin_hold === 1;
  const hold = useAdminUserHoldMutation();
  const [reason, setReason] = useState('');

  const handleConfirm = () => {
    toast.promise(
      hold.mutateAsync({ uid: user.uid, hold: !isSuspended, reason: reason.trim() || undefined }),
      {
        loading: isSuspended ? 'Lifting suspension…' : 'Suspending user…',
        success: () => {
          onDone();
          return isSuspended ? `Suspension lifted for ${fullName}` : `${fullName} suspended`;
        },
        error: err => (err as Error).message || 'Action failed',
      }
    );
  };

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>
          {isSuspended ? `Reactivate ${fullName}?` : `Suspend ${fullName}?`}
        </AlertDialogTitle>
        <AlertDialogDescription>
          {isSuspended
            ? 'Lifts the admin hold — the user can transact again.'
            : 'Sets admin_hold — the user can no longer transact (they can still sign in). The account is otherwise intact.'}
        </AlertDialogDescription>
      </AlertDialogHeader>

      <div className="space-y-1 py-1">
        <Label htmlFor={`hold-reason-${user.uid}`}>Reason (optional)</Label>
        <Input
          id={`hold-reason-${user.uid}`}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Recorded in the audit log"
        />
      </div>

      <AlertDialogFooter>
        <AlertDialogCancel onClick={onDone}>Cancel</AlertDialogCancel>
        <AlertDialogAction
          onClick={e => {
            e.preventDefault();
            handleConfirm();
          }}
          disabled={hold.isPending}
          className={isSuspended ? 'bg-green-600 hover:bg-green-700' : 'bg-amber-600 hover:bg-amber-700'}
        >
          {hold.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          {isSuspended ? 'Reactivate' : 'Suspend'}
        </AlertDialogAction>
      </AlertDialogFooter>
    </>
  );
};

export default UserRowActions;
